// Live preview cards rendered in the SAME style as the public site.
// When `onPositionChange` is provided, the image is draggable to reposition
// the visible part of the photo (applies CSS object-position).

import { useRef } from "react";
import { Quote, Move } from "lucide-react";

function DraggableImage({ src, alt, position, onPositionChange, className = "", overlayChildren }) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const parse = (s) => {
    const m = (s || "50% 50%").match(/([-\d.]+)%\s+([-\d.]+)%/);
    return m ? [parseFloat(m[1]), parseFloat(m[2])] : [50, 50];
  };

  const onPointerDown = (e) => {
    if (!onPositionChange) return;
    const rect = containerRef.current.getBoundingClientRect();
    const [px, py] = parse(position);
    dragRef.current = { sx: e.clientX, sy: e.clientY, px, py, w: rect.width, h: rect.height };
    try { containerRef.current.setPointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { sx, sy, px, py, w, h } = dragRef.current;
    const nx = Math.max(0, Math.min(100, px - ((e.clientX - sx) / w) * 100));
    const ny = Math.max(0, Math.min(100, py - ((e.clientY - sy) / h) * 100));
    onPositionChange(`${nx.toFixed(1)}% ${ny.toFixed(1)}%`);
  };

  const onPointerUp = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { containerRef.current.releasePointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
  };

  return (
    <div
      ref={containerRef}
      className={`${className} ${onPositionChange ? "cursor-grab active:cursor-grabbing select-none" : ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: onPositionChange ? "none" : undefined }}
      data-testid="draggable-image"
    >
      {src ? (
        <img
          src={src}
          alt={alt || "Preview"}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: position || "50% 50%" }}
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--pov-bg-3)] grid place-items-center text-white/30 text-xs pointer-events-none">No image</div>
      )}
      {onPositionChange && src ? (
        <div className="absolute top-2 right-2 px-2 py-1 text-[10px] rounded-sm bg-black/60 text-white/80 flex items-center gap-1 pointer-events-none">
          <Move className="w-3 h-3" /> Drag to reposition
        </div>
      ) : null}
      {overlayChildren}
    </div>
  );
}

export function PortfolioPreview({ item, onPositionChange }) {
  return (
    <article className="relative overflow-hidden rounded-sm glass" data-testid="preview-portfolio">
      <DraggableImage
        src={item.image_data}
        alt={item.title}
        position={item.image_position}
        onPositionChange={onPositionChange}
        className="relative aspect-[4/3] overflow-hidden"
        overlayChildren={
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent pointer-events-none" />
            <div className="absolute left-5 bottom-5 right-5 pointer-events-none">
              {item.field ? <p className="overline">{item.field}</p> : null}
              {item.event_date ? <p className="text-[10px] text-white/55 mt-0.5 tracking-wide">{item.event_date}</p> : null}
              <p className="font-display text-lg mt-1">{item.title || "Untitled"}</p>
              {item.description ? (
                <p className="text-xs text-white/65 mt-1 line-clamp-2">{item.description}</p>
              ) : null}
            </div>
          </>
        }
      />
    </article>
  );
}

export function RoomPreview({ item, onPositionChange }) {
  return (
    <div className="space-y-4" data-testid="preview-room">
      <DraggableImage
        src={item.image_data}
        alt={item.title}
        position={item.image_position}
        onPositionChange={onPositionChange}
        className="relative aspect-[5/4] overflow-hidden rounded-sm glass"
        overlayChildren={
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#030712]/60 via-transparent to-transparent pointer-events-none" />
            {item.field ? (
              <div className="absolute left-3 top-3 pointer-events-none">
                <span className="px-2 py-1 text-[10px] rounded-sm glass-strong text-[var(--pov-cyan)] uppercase tracking-wider">
                  {item.field}
                </span>
              </div>
            ) : null}
            {(item.location || item.event_date) ? (
              <div className="absolute right-3 top-3 flex flex-wrap items-center gap-1.5 justify-end pointer-events-none">
                {item.location ? (
                  <span className="px-2 py-1 text-[10px] rounded-sm glass-strong text-white/70">
                    {item.location}
                  </span>
                ) : null}
                {item.event_date ? (
                  <span className="px-2 py-1 text-[10px] rounded-sm glass-strong text-white/50">
                    {item.event_date}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="absolute left-3 bottom-3 right-3 pointer-events-none">
              <p className="font-display text-lg leading-tight">{item.title || "Untitled"}</p>
            </div>
          </>
        }
      />

      <div className="glass rounded-sm p-5">
        <Quote className="w-4 h-4 text-[var(--pov-coral)]" />
        <p className="mt-3 text-sm text-white/80 leading-relaxed">
          {item.story || <span className="text-white/30 italic">Story preview…</span>}
        </p>
        {item.outcome && item.outcome.length > 0 ? (
          <div className="mt-5 hairline pt-4">
            <p className="overline mb-3">Outcome</p>
            <ul className="space-y-2">
              {item.outcome.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-white/75">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-[var(--pov-cyan)] shrink-0" />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {item.tag ? <p className="mt-4 text-[10px] text-white/40">{item.tag}</p> : null}
      </div>
    </div>
  );
}
